import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { startOfTomorrow } from 'date-fns';

import { DatepickerRange, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { useMutateUpdateAssetCartItem } from '@hooks/assets/useMutateUpdateAssetCartItem';
import { useQueryRentRequestsDatesPickedUpdate } from '@hooks/assets/useQueryRentRequestsDatesPickedUpdate';

import { endOfDayInUTC, startOfDayInUTC } from '@utils/kpi-utils/date';

import { EditStartEndDateFormValues } from '@interfaces/rent/assetRent';

import { EValidationMode } from '@enums/validationMode';

import { validationSchema } from './schema';

import styles from './EditStartEndDateForm.module.scss';

interface IEditStartEndDateForm {
	closeModal: () => void;
	setIsDirtyForm: Dispatch<SetStateAction<boolean>>;
	assetIdForEdit: string;
	assetDbIdForEdit: string;
}

const EditStartEndDateForm = ({
	closeModal,
	setIsDirtyForm,
	assetIdForEdit,
	assetDbIdForEdit,
}: IEditStartEndDateForm) => {
	const {
		clearErrors,
		formState: { errors, isDirty },
		handleSubmit,
		setValue,
		watch,
	} = useForm<EditStartEndDateFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(validationSchema),
	});

	const {
		handleUpdateAssetCartItem,
		isUpdatingAssetCartItem,
	} = useMutateUpdateAssetCartItem([closeModal]);

	const {
		rentRequestsDatesPicked,
		isLoadingRentRequestsDatesPicked,
	} = useQueryRentRequestsDatesPickedUpdate(assetIdForEdit);

	const setFromDate = (date: number | null) => {
		if (date) clearErrors('rentalPeriodFrom');

		setValue('rentalPeriodFrom', date, { shouldDirty: true });
	};
	const setToDate = (date: number | null) => {
		if (date) clearErrors('rentalPeriodTo');

		setValue('rentalPeriodTo', date, { shouldDirty: true });
	};

	const onSubmit = (values: EditStartEndDateFormValues) => {
		const from = values?.rentalPeriodFrom ? startOfDayInUTC(new Date(values.rentalPeriodFrom)) : null;
		const to = values?.rentalPeriodTo ? endOfDayInUTC(new Date(values.rentalPeriodTo)) : null;

		handleUpdateAssetCartItem({
			assetId: assetIdForEdit,
			dto: {
				assetDbId: assetDbIdForEdit,
				from,
				to,
			},
		});
	};

	useEffect(() => setIsDirtyForm(isDirty), [isDirty, setIsDirtyForm]);

	return (
		<>
			{isUpdatingAssetCartItem && isLoadingRentRequestsDatesPicked && <SpinnerOverlay />}
			<div className={styles.datePickersContainer}>
				<DatepickerRange
					disableInput
					errorFromDate={errors?.rentalPeriodFrom?.message}
					errorToDate={errors?.rentalPeriodTo?.message}
					excludeDateIntervalsFrom={rentRequestsDatesPicked}
					excludeDateIntervalsTo={rentRequestsDatesPicked}
					groupLabel='Rental Period'
					handleChangeFromDate={setFromDate}
					handleChangeToDate={setToDate}
					isMobileDevice={false}
					isTodayButtonFrom={false}
					isTodayButtonTo={false}
					minDateFrom={startOfTomorrow()}
					minDateTo={startOfTomorrow()}
					requiredFromDate
					requiredToDate
					value={{
						from: watch('rentalPeriodFrom') as number,
						to: watch('rentalPeriodTo') as number,
					}}
				/>
			</div>
			<SubmissionButtons
				negativeHandler={closeModal}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Update'
			/>
		</>
	);
};

export default EditStartEndDateForm;
